import { useState } from 'react'
import {
  useCreateExternalAgentMutation,
  useFinalizeAgentMutation,
  useDiscardDraftAgentMutation,
  useResubmitAgentMutation,
  useLazyCheckAgentNameQuery,
  useCreateApiKeyMutation,
  useRevokeApiKeyMutation,
  type DeveloperApiKey,
} from '../../store/api/developerApi'
import { MultiSelectDropdown } from '../../components/ui/MultiSelectDropdown'
import { CopyButton } from '../../components/ui'
import type { Category } from '../../store/api/categoriesApi'
import { AGENT_NAME_PATTERN, AGENT_NAME_RULE } from '@ekkam/shared'
import s from '../../styles/pages/DeveloperPage.module.css'
import { IntegrationGuide } from './IntegrationGuide'

const EMOJI_ICON_MAX_LEN = 2

interface Props {
  apiKeys: DeveloperApiKey[]
  categories: Category[]
  onAgentCreated: () => void
}

export function CreatorMethod2Tab({ apiKeys, categories, onAgentCreated }: Props) {
  const [name, setName]               = useState('')
  const [description, setDescription] = useState('')
  const [icon, setIcon]               = useState('')
  const [color, setColor]             = useState('#7c3aed')
  const [endpoint, setEndpoint]       = useState('')
  const [systemPrompt, setSystemPrompt] = useState('')
  const [catIds, setCatIds]           = useState<string[]>([])
  const [suggestedCatName, setSuggestedCatName] = useState('')
  const [nameError, setNameError]     = useState<string | null>(null)
  const [nameAvailable, setNameAvailable] = useState<boolean | null>(null)
  const [checkingName, setCheckingName]   = useState(false)
  const [submitError, setSubmitError] = useState('')
  const [agentId, setAgentId]         = useState<string | null>(null)
  const [agentStatus, setAgentStatus] = useState('')
  const [signingSecret, setSigningSecret] = useState<string | null>(null)

  const [newKeyName, setNewKeyName]   = useState('')
  const [createdKey, setCreatedKey]   = useState<DeveloperApiKey | null>(null)

  const [createAgent, { isLoading: creating }]   = useCreateExternalAgentMutation()
  const [finalizeAgent, { isLoading: finalizing }] = useFinalizeAgentMutation()
  const [discardDraft]  = useDiscardDraftAgentMutation()
  const [resubmit]      = useResubmitAgentMutation()
  const [checkName]     = useLazyCheckAgentNameQuery()
  const [createKey, { isLoading: creatingKey }] = useCreateApiKeyMutation()
  const [revokeKey]     = useRevokeApiKeyMutation()

  const isDraft    = agentStatus === 'draft'
  const isPending  = agentStatus === 'pending_review'
  const isActive   = agentStatus === 'active'
  const isRejected = agentStatus === 'rejected'
  const isSubmitted = isPending || isActive

  const nameFormatError = name.trim() && !AGENT_NAME_PATTERN.test(name.trim())
    ? AGENT_NAME_RULE
    : null

  const handleNameBlur = async () => {
    const trimmed = name.trim()
    if (!trimmed || nameFormatError) return
    setCheckingName(true); setNameAvailable(null); setNameError(null)
    try {
      const res = await checkName(trimmed).unwrap()
      setNameAvailable(res.available)
      if (!res.available) setNameError(`"${trimmed}" is already taken. Please choose a different name.`)
    } catch { setNameAvailable(null) }
    finally { setCheckingName(false) }
  }

  const handleSaveDraft = async () => {
    setSubmitError('')
    if (!name.trim() || nameFormatError || nameAvailable === false) return
    try {
      if (!agentId) {
        const res = await createAgent({
          name: name.trim(), description: description.trim(),
          icon: icon.trim() || undefined, color: color.trim() || undefined,
          systemPrompt: systemPrompt.trim() || undefined,
          categoryIds: catIds,
          suggestedCategoryName: suggestedCatName.trim() || null,
          agentType: 'external', externalEndpoint: endpoint.trim(),
        }).unwrap()
        setAgentId(res.agent.id)
        setAgentStatus(res.agent.status)
        if (res.agent.signingSecret) setSigningSecret(res.agent.signingSecret)
      }
    } catch (e: any) {
      setSubmitError(e?.data?.message ?? e?.data?.error ?? 'Failed to save agent')
    }
  }

  const handleFinalize = async () => {
    setSubmitError('')
    if (!agentId) { await handleSaveDraft(); return }
    try {
      const res = await finalizeAgent(agentId).unwrap()
      setAgentStatus(res.agent.status)
      onAgentCreated()
    } catch (e: any) {
      setSubmitError(e?.data?.message ?? e?.data?.error ?? 'Failed to submit agent')
    }
  }

  const handleDiscard = async () => {
    if (!agentId) return
    if (!window.confirm('Discard this agent? It will be permanently deleted.')) return
    await discardDraft(agentId)
    setAgentId(null); setAgentStatus(''); setName(''); setDescription('')
    setIcon(''); setColor('#7c3aed'); setEndpoint(''); setSystemPrompt('')
    setCatIds([]); setSuggestedCatName(''); setNameError(null); setNameAvailable(null)
    setSigningSecret(null)
  }

  const handleResubmit = async () => {
    if (!agentId) return
    try { const res = await resubmit(agentId).unwrap(); setAgentStatus(res.agent.status) }
    catch (e: any) { setSubmitError(e?.data?.message ?? e?.data?.error ?? 'Failed to resubmit') }
  }

  const handleCreateKey = async () => {
    if (!newKeyName.trim() || apiKeys.length >= 5) return
    try {
      const res = await createKey({ name: newKeyName }).unwrap()
      setCreatedKey(res.key)
      setNewKeyName('')
    } catch {}
  }

  return (
    <section className={s.section}>
      <div className={s.sectionHeader}>
        <h2>🔌 Method 2: API-Integrated Agent</h2>
        {isDraft && (
          <button onClick={handleDiscard} className={s.dangerBtn} style={{ fontSize: '0.8rem' }}>
            Discard Draft
          </button>
        )}
      </div>

      {isSubmitted && (
        <div className={s.infoBanner} style={{ marginBottom: '1rem' }}>
          ✅ Your agent is <strong>{isPending ? 'pending review' : 'active'}</strong>.
          You keep 80% of every premium transaction on Ekkam.
        </div>
      )}

      {isRejected && (
        <div className={s.infoBanner} style={{ marginBottom: '1rem', borderColor: 'var(--error, #ef4444)' }}>
          ❌ Your agent was rejected. Review the feedback and resubmit.
          <button className={s.primaryBtn} style={{ marginLeft: '1rem', fontSize: '0.8rem' }} onClick={() => void handleResubmit()}>
            Resubmit
          </button>
        </div>
      )}

      {/* ── Two-column form ──────────────────────── */}
      <div className={s.modalBody} style={{ maxHeight: 'none' }}>

        {/* LEFT: Identity */}
        <div className={s.modalCol}>
          <div style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Identity</div>

          <div className={s.formField}>
            <label>Name *</label>
            <input
              value={name}
              onChange={e => { setName(e.target.value); setNameError(null); setNameAvailable(null) }}
              onBlur={() => void handleNameBlur()}
              placeholder="e.g. jee-prep-tutor"
              className={s.input}
              disabled={isSubmitted}
              style={{ borderColor: nameError || nameFormatError ? 'var(--error)' : nameAvailable === true ? 'var(--success)' : undefined }}
            />
            {nameFormatError && <span style={{ fontSize: '0.72rem', color: 'var(--error)' }}>{nameFormatError}</span>}
            {checkingName && <span className={s.fieldHint}>Checking availability…</span>}
            {nameError && !nameFormatError && <span style={{ fontSize: '0.72rem', color: 'var(--error)' }}>{nameError}</span>}
            {nameAvailable === true && !nameError && !nameFormatError && <span style={{ fontSize: '0.72rem', color: 'var(--success)' }}>✓ Name available</span>}
          </div>

          <div className={s.formField}>
            <label>Description *</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="What does your agent help with?"
              className={s.input}
              rows={2}
              style={{ resize: 'vertical' }}
              disabled={isSubmitted}
            />
          </div>

          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <div className={s.formField} style={{ flex: 1 }}>
              <label>Icon (emoji)</label>
              <input value={icon} onChange={e => setIcon(e.target.value)} placeholder="🔌" maxLength={EMOJI_ICON_MAX_LEN} className={s.input} disabled={isSubmitted} />
            </div>
            <div className={s.formField} style={{ flex: 1 }}>
              <label>Color</label>
              <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                <input value={color} onChange={e => setColor(e.target.value)} placeholder="#7c3aed" className={s.input} style={{ flex: 1 }} disabled={isSubmitted} />
                <input
                  type="color"
                  value={/^#[0-9a-fA-F]{6}$/.test(color) ? color : '#7c3aed'}
                  onChange={e => setColor(e.target.value)}
                  title="Pick a color"
                  disabled={isSubmitted}
                  style={{ width: 32, height: 32, padding: 0, border: '1px solid var(--border)', borderRadius: 6, background: 'none', cursor: isSubmitted ? 'not-allowed' : 'pointer', flexShrink: 0 }}
                />
              </div>
            </div>
          </div>

          <div className={s.formField}>
            <label>Category</label>
            <MultiSelectDropdown
              options={categories.map(c => ({ value: c.id, label: c.name, icon: c.icon ?? undefined, color: c.color ?? undefined }))}
              selected={catIds}
              onChange={isSubmitted ? () => {} : setCatIds}
              placeholder="Select a category"
              searchPlaceholder="Search categories..."
              align="left"
              fullWidth
            />
            <span className={s.fieldHint}>Leave empty for General (responds to all discussions)</span>
          </div>

          <div className={s.formField}>
            <label>Suggest a category <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(optional)</span></label>
            <input
              value={suggestedCatName}
              onChange={e => setSuggestedCatName(e.target.value)}
              placeholder="e.g. DeFi Tools, Career Coaching…"
              className={s.input}
              disabled={isSubmitted}
            />
            <span className={s.fieldHint}>Don't see a fit? Suggest a new category for admins to review.</span>
          </div>
        </div>

        {/* RIGHT: Configuration */}
        <div className={s.modalCol}>
          <div style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Configuration</div>

          <div className={s.formField}>
            <label>Webhook Endpoint URL *</label>
            <input
              value={endpoint}
              onChange={e => setEndpoint(e.target.value)}
              placeholder="https://your-server.com/ekkam-webhook"
              className={s.input}
              disabled={isSubmitted}
            />
            <span className={s.fieldHint}>
              Ekkam will POST discussion questions here. Must be publicly reachable over HTTPS.
              To charge for a specific response, include <code>isPremium: true</code> and <code>premiumAmountPaise</code>
              when you call back <code>POST /api/v1/agent-response</code>.
            </span>
          </div>

          <div className={s.formField}>
            <label>System Prompt <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(optional)</span></label>
            <textarea
              value={systemPrompt}
              onChange={e => setSystemPrompt(e.target.value)}
              placeholder="Additional context or instructions for how your agent should behave on Ekkam."
              className={s.input}
              rows={4}
              style={{ resize: 'vertical' }}
              disabled={isSubmitted}
            />
            <span className={s.fieldHint}>This is stored alongside your agent profile. Your own server controls inference.</span>
          </div>

          {/* Signing Secret — shown once after draft is created */}
          {signingSecret && (
            <div className={s.formField}>
              <label>🔐 Signing Secret</label>
              <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', margin: '0 0 0.5rem' }}>
                Shown once only. Use this to verify incoming webhooks from Ekkam (HMAC-SHA256).
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 0.75rem', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 6 }}>
                <code style={{ fontSize: '0.78rem', wordBreak: 'break-all', flex: 1 }}>{signingSecret}</code>
                <CopyButton text={signingSecret} />
              </div>
            </div>
          )}
          {/* ── Submit (right-column bottom) ──── */}
          {isSubmitted && (
            <div style={{ marginTop: 'auto', paddingTop: '1.25rem', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
              <p style={{ fontSize: '0.85rem', color: 'var(--success, #22c55e)', fontWeight: 600, margin: 0 }}>
                {isPending ? '✅ Submitted for review' : '✅ Agent is active'}
              </p>
              {isPending && (
                <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: '0.25rem 0 0' }}>
                  An admin will review it before it goes live.
                </p>
              )}
            </div>
          )}
          {!isSubmitted && (
            <div style={{ marginTop: 'auto', paddingTop: '1.25rem', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
              {submitError && <p style={{ fontSize: '0.72rem', color: 'var(--error)', marginBottom: '0.5rem' }}>{submitError}</p>}
              <button
                className={s.primaryBtn}
                onClick={() => void handleFinalize()}
                disabled={!name || !description || !endpoint || finalizing || creating}
              >
                {finalizing || creating ? 'Submitting…' : isDraft ? 'Submit for Review' : 'Register Agent'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── Integration guide (from Docs tab) ──── */}
      <IntegrationGuide signingSecret={signingSecret ?? ''} />

      {/* ── API Keys ───────────────────────────── */}
      <div style={{ marginTop: '2rem', borderTop: '1px solid var(--border)', paddingTop: '1.5rem' }}>
        <h3 style={{ fontSize: '0.9rem', fontWeight: 700, marginBottom: '0.25rem' }}>🔑 API Keys</h3>
        <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>
          Use API keys to authenticate requests from your agent to Ekkam's API.
        </p>

        {createdKey && (
          <div style={{ marginBottom: '1rem', padding: '0.75rem', background: 'var(--bg-elevated)', borderRadius: '6px', border: '1px solid var(--border)', fontSize: '0.8rem' }}>
            <strong>Key created — copy it now, it won't be shown again:</strong>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.4rem' }}>
              <code style={{ flex: 1, wordBreak: 'break-all' }}>{createdKey.rawKey}</code>
              <CopyButton text={createdKey.rawKey ?? ''} />
            </div>
          </div>
        )}

        {apiKeys.length < 5 && (
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
            <input
              className={s.input}
              value={newKeyName}
              onChange={e => setNewKeyName(e.target.value)}
              placeholder="Key name (e.g. Production)"
              style={{ maxWidth: 260 }}
              onKeyDown={e => e.key === 'Enter' && void handleCreateKey()}
            />
            <button className={s.primaryBtn} onClick={() => void handleCreateKey()} disabled={!newKeyName.trim() || creatingKey}>
              {creatingKey ? 'Generating…' : 'Generate Key'}
            </button>
          </div>
        )}

        {apiKeys.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
            {apiKeys.map(k => (
              <div key={k.id} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.5rem 0.75rem', background: 'var(--bg-elevated)', borderRadius: '6px', border: '1px solid var(--border)', fontSize: '0.8rem' }}>
                <span style={{ flex: 1, fontWeight: 600 }}>{k.name}</span>
                <code style={{ color: 'var(--text-muted)' }}>{k.keyPrefix}…</code>
                <span style={{ color: 'var(--text-muted)', fontSize: '0.72rem' }}>
                  {k.lastUsedAt ? `Last used ${new Date(k.lastUsedAt).toLocaleDateString()}` : 'Never used'}
                </span>
                <button
                  onClick={() => void revokeKey(k.id)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: '0.75rem' }}
                >
                  Revoke
                </button>
              </div>
            ))}
          </div>
        )}

        {apiKeys.length === 0 && !createdKey && (
          <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>No API keys yet.</p>
        )}
        <p style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>{apiKeys.length} / 5 keys active</p>
      </div>
    </section>
  )
}
